import React from "react"
import SubscriptionFeatureAccess from "../../subscriptions/components/SubscriptionFeatureAccess"
import RankingPanel from "./RankingPanel"
import WeeklyForecastPanel from "./WeeklyForecastPanel"
import MonthlyForecastPanel from "./MonthlyForecastPanel"
import SaturationPanel from "./SaturationPanel"
import HeatmapPanel from "./HeatmapPanel"

export default function OperativeAdvancedSection({
  effectiveCanAdvancedReports,
  currentPlanName,
  openUpgrade,
  isMobile,
  collapsedSections,
  toggleSection,
  loading,
  doctorRanking,
  treatmentRanking,
  weeklyForecast,
  monthlyForecast,
  saturation,
  heatmap,
  getSatBg,
  getSatColor,
  getSatLabel,
  getHeatColor,
  formatMoney,
  formatPct
}) {
  const doctors = Array.isArray(doctorRanking) ? doctorRanking : []
  const treatments = Array.isArray(treatmentRanking) ? treatmentRanking : []

  const hasDoctorRanking = doctors.length > 0
  const hasTreatmentRanking = treatments.length > 0

  const hasWeeklyForecast = hasStructuredContent(weeklyForecast)
  const hasMonthlyForecast = hasStructuredContent(monthlyForecast)
  const hasSaturation = hasStructuredContent(saturation)
  const hasHeatmap = hasStructuredContent(heatmap)

  const shouldShowRankingRow =
    loading || hasDoctorRanking || hasTreatmentRanking

  const shouldShowForecastRow =
    loading || hasWeeklyForecast || hasMonthlyForecast

  const shouldShowSaturation =
    loading || hasSaturation

  const shouldShowHeatmap =
    loading || hasHeatmap

  const rankingPanelsCount =
    Number(loading || hasDoctorRanking) + Number(loading || hasTreatmentRanking)

  const forecastPanelsCount =
    Number(loading || hasWeeklyForecast) + Number(loading || hasMonthlyForecast)

  return (
    <SubscriptionFeatureAccess
      enabled={effectiveCanAdvancedReports}
      featureKey="advancedReports"
      planName={currentPlanName}
      subtitle="Desbloquea ranking, pronósticos, saturación y análisis avanzado de la agenda."
      onUpgrade={() => openUpgrade("advancedReports")}
    >
      <>
        {shouldShowRankingRow && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                isMobile || rankingPanelsCount <= 1
                  ? "1fr"
                  : "minmax(0, 1fr) minmax(0, 1fr)",
              gap: 16,
              marginBottom: 16,
              alignItems: "start"
            }}
          >
            {(loading || hasDoctorRanking) && (
              <RankingPanel
                title="Ranking de doctores"
                subtitle="Quién produce mejor hoy"
                collapsed={!!collapsedSections.doctorRanking}
                onToggle={() => toggleSection("doctorRanking")}
                loading={loading}
                emptyText="Sin ranking de doctores."
                items={doctors.map((d) => ({
                  key: d.doctorId,
                  title: d.doctorName,
                  detail: `Producción: $${formatMoney(d.production)} · Tratamientos: ${formatMoney(d.treatments)}`
                }))}
              />
            )}

            {(loading || hasTreatmentRanking) && (
              <RankingPanel
                title="Ranking de tratamientos"
                subtitle="Qué mueve más producción"
                collapsed={!!collapsedSections.treatmentRanking}
                onToggle={() => toggleSection("treatmentRanking")}
                loading={loading}
                emptyText="Sin ranking de tratamientos."
                items={treatments.map((t) => ({
                  key: t.treatmentId,
                  title: t.treatmentName,
                  detail: `Producción: $${formatMoney(t.production)} · Veces aplicado: ${formatMoney(t.count)}`
                }))}
              />
            )}
          </div>
        )}

        {shouldShowForecastRow && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                isMobile || forecastPanelsCount <= 1
                  ? "1fr"
                  : "minmax(0, 1.25fr) minmax(280px, 0.75fr)",
              gap: 16,
              marginBottom: 16,
              alignItems: "start"
            }}
          >
            {(loading || hasWeeklyForecast) && (
              <WeeklyForecastPanel
                weeklyForecast={weeklyForecast}
                collapsed={!!collapsedSections.weeklyForecast}
                onToggle={() => toggleSection("weeklyForecast")}
                loading={loading}
                isMobile={isMobile}
                formatMoney={formatMoney}
              />
            )}

            {(loading || hasMonthlyForecast) && (
              <MonthlyForecastPanel
                monthlyForecast={monthlyForecast}
                collapsed={!!collapsedSections.monthlyForecast}
                onToggle={() => toggleSection("monthlyForecast")}
                formatMoney={formatMoney}
              />
            )}
          </div>
        )}

        {shouldShowSaturation && (
          <SaturationPanel
            saturation={saturation}
            collapsed={!!collapsedSections.saturation}
            onToggle={() => toggleSection("saturation")}
            loading={loading}
            getSatBg={getSatBg}
            getSatColor={getSatColor}
            getSatLabel={getSatLabel}
            formatPct={formatPct}
          />
        )}

        {shouldShowHeatmap && (
          <HeatmapPanel
            heatmap={heatmap}
            collapsed={!!collapsedSections.heatmap}
            onToggle={() => toggleSection("heatmap")}
            loading={loading}
            isMobile={isMobile}
            getHeatColor={getHeatColor}
            formatMoney={formatMoney}
          />
        )}
      </>
    </SubscriptionFeatureAccess>
  )
}

function hasStructuredContent(value) {
  if (Array.isArray(value)) {
    return value.length > 0
  }

  if (!value || typeof value !== "object") {
    return Boolean(value)
  }

  return Object.values(value).some((item) => {
    if (Array.isArray(item)) return item.length > 0
    if (item && typeof item === "object") return Object.keys(item).length > 0
    return item !== null && item !== undefined && item !== ""
  })
}